import { DetailPageLayout } from '@app/DetailPageLayout';
import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import { getAccessMode } from '@common/detailPage.hooks';
import { useAppConfigs, useHasPermission, useProjectIsArchived } from '@config/config.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { ComponentType, Form, Grid, IBreadcrumbItem, IconName, Scrollbar, useForm } from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import { useCallback, useEffect, useMemo } from 'react';
import usePrompt from '@common/prompt.hooks';
import { ProjectRequestDetailFormGeneralInfo } from '@projectRequest/ProjectRequestDetailFormGeneralInfo';
import {
  EProjectRequestFormKeys,
  mockDataProjectRegion,
  mockDataProjectType,
} from '@projectRequest/projectRequest.models';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { ERolePermission } from '@userProfile/permission/permission.constants';
import { useConfirmationModal } from '@common/confirmation.hooks';
import {
  useArchiveProject,
  useGetProjectByProjectNoAsync,
  useUnarchiveProject,
  useUpdateProject,
} from './project.queries';
import { IProject, IUpdateProjectRequest } from './project.models';

export const ProjectDetailPage = () => {
  const { id, mode } = getAccessMode();
  const appConfigs = useAppConfigs();
  const isProjectArchived = useProjectIsArchived();
  const isUserRightAdmin = useHasPermission(ERolePermission.ADMINISTRATOR);
  const isEm = useHasPermission(ERolePermission.ENGINEERING_MANAGER);

  const queryClient = useQueryClient();

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const { navigate, projectNo } = useNavigate();

  const isReadOnly = mode === 'View' || !isUserRightAdmin || isProjectArchived;

  const {
    data: dataProject,
    isLoading: isLoadingProject,
    error: errorProject,
  } = useGetProjectByProjectNoAsync(appConfigs?.configs.projectNoFromUrl, false);
  const project = dataProject?.item;
  const {
    isLoading: isLoadingUpdateProject,
    isError: isErrorUpdateProject,
    isSuccess: isSuccessUpdateProject,
    error: errorUpdateProject,
    reset: resetUpdateProject,
    mutate: mutateUpdateProject,
  } = useUpdateProject();

  const {
    isLoading: isLoadingArchiveProject,
    isSuccess: isSuccessArchiveProject,
    isError: isErrorArchiveProject,
    error: errorArchiveProject,
    reset: resetArchiveProject,
    mutate: mutateArchiveProject,
  } = useArchiveProject();

  const {
    isLoading: isLoadingUnarchiveProject,
    isSuccess: isSuccessUnarchiveProject,
    isError: isErrorUnarchiveProject,
    error: errorUnarchiveProject,
    reset: resetUnarchiveProject,
    mutate: mutateUnarchiveProject,
  } = useUnarchiveProject();

  const { renderModal: renderArchiveConfirmation, openPortal: openArchiveConfirmation } = useConfirmationModal({
    content: project?.isArchived
      ? `Confirm to unarchive project? All forms will be activated and Users can edit it.`
      : 'Confirm to archive project? All forms will be diabled and users cannot perform any action.',
    onConfirm: () => {
      if (!project?.id || project.id < 0) return;
      if (project?.isArchived) mutateUnarchiveProject(project.id);
      else mutateArchiveProject(project?.id);
    },
  });

  useEffect(() => {
    if (dataProject?.item)
      appConfigs.updateConfigs?.((configs) => {
        return { ...configs, project: dataProject?.item };
      });
  }, [JSON.stringify(dataProject)]);

  const getDefaultFormValues = (projectLocal?: IProject) => {
    const typeOption = mockDataProjectType.find((type) => type.id === projectLocal?.typeId);
    const regionOption = mockDataProjectRegion.find((region) => region.id === projectLocal?.regionId);
    const state = {
      [EProjectRequestFormKeys.PROJECT_NO]: projectLocal?.projectNo ?? null,
      [EProjectRequestFormKeys.PROJECT_TYPE]: typeOption ?? null,
      [EProjectRequestFormKeys.PROJECT_REGION]: regionOption ?? null,
      [EProjectRequestFormKeys.PROJECT_TITLE]: projectLocal?.projectName ?? null,
      [EProjectRequestFormKeys.ADDRESS]: projectLocal?.address ?? null,
      [EProjectRequestFormKeys.DESCRIPTION]: projectLocal?.description ?? null,
      [EProjectRequestFormKeys.TEL]: projectLocal?.tel ?? null,
      [EProjectRequestFormKeys.FAX]: projectLocal?.fax ?? null,
      [EProjectRequestFormKeys.EMAIL]: projectLocal?.email ?? null,
      [EProjectRequestFormKeys.START_DATE]: projectLocal?.startDate ?? '',
      [EProjectRequestFormKeys.END_DATE]: projectLocal?.finishDate ?? '',
    };

    return state;
  };

  const handleSubmit = useCallback(
    (data: any) => {
      const request: IUpdateProjectRequest = {
        ...data,
        id: project?.id,
        rowVersion: project?.rowVersion,
        typeId: data[EProjectRequestFormKeys.PROJECT_TYPE]?.id,
        regionId: data[EProjectRequestFormKeys.PROJECT_REGION]?.id,
      };
      mutateUpdateProject(request);
    },
    [JSON.stringify(project)],
  );

  const breadcrumbItems: IBreadcrumbItem[] = [
    {
      text: `${projectNo} - Home`,
      prefixIconName: 'House',
      onClick: () => {
        navigate(routes.t1.path);
      },
    },
    {
      text: `${mode} Project Info`,
      active: true,
      prefixIconName: 'JournalText' as IconName,
    },
  ];
  const topBarLeftItems: IDetailPageTopBarItem[] = useMemo(() => {
    return [
      {
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Primary,
        isError: isErrorUpdateProject,
        isLoading: isLoadingUpdateProject,
        isSuccess: isSuccessUpdateProject,
        title: 'Save',
        buttonIconName: 'FolderCheck' as IconName,
        onClick: () => {
          useFormMethods.handleSubmit(handleSubmit)();
        },
        showNoneInReadOnly: true,
        disable: isLoadingUpdateProject,
      },
      ...(isEm
        ? [
            {
              type: EDetailPageTopBarItemType.loadingButton,
              componentType: ComponentType.Danger,
              isError: isErrorArchiveProject || isErrorUnarchiveProject,
              isLoading: isLoadingArchiveProject || isLoadingUnarchiveProject,
              isSuccess: isSuccessArchiveProject || isSuccessUnarchiveProject,
              title: project?.isArchived ? 'Unarchive' : 'Archive',
              buttonIconName: 'FolderCheck' as IconName,
              onClick: () => {
                openArchiveConfirmation();
              },
              disable: isLoadingArchiveProject || isLoadingUnarchiveProject,
              showNoneInArchivedProject: false,
            },
          ]
        : []),
    ];
  }, [
    isErrorUpdateProject,
    isSuccessUpdateProject,
    isLoadingUpdateProject,
    isLoadingArchiveProject,
    isSuccessArchiveProject,
    isErrorArchiveProject,
    isLoadingUnarchiveProject,
    isSuccessUnarchiveProject,
    isErrorUnarchiveProject,
    handleSubmit,
    JSON.stringify(project),
    isEm,
  ]);
  return (
    <Scrollbar>
      <ApiErrorDisplay
        errors={[
          { error: errorUpdateProject, genericError: 'Update project info failed', reset: resetUpdateProject },
          { error: errorArchiveProject, genericError: 'Archive project failed', reset: resetArchiveProject },
          { error: errorUnarchiveProject, genericError: 'Unarchive project failed', reset: resetUnarchiveProject },
        ]}
      />
      <ApiSuccessToast
        successes={[
          { isSuccess: isSuccessUpdateProject, message: 'Update project info success', reset: resetUpdateProject },
          { isSuccess: isSuccessArchiveProject, message: 'Archive project success', reset: resetArchiveProject },
          { isSuccess: isSuccessUnarchiveProject, message: 'Unarchive project success', reset: resetUnarchiveProject },
        ]}
        callbackAfterToasted={() => {
          queryClient.invalidateQueries([FETCH_QUERIES_NAME.PROJECT_DATA_ALL]);
        }}
      />
      <DetailPageLayout
        errorGetDetail={null}
        // isRestricted={isRestricted}
        data={<></>}
        isDrawer={false}
        isRefetching={false}
        isLoading={false}
        title={`Project Info ${project?.projectNo} - ${project?.projectName}`}
        breadcrumb={breadcrumbItems}
        render={(DetailPageRightMenuOpener) => {
          return (
            <>
              <Form
                enableUnsavedChangesPrompt
                unsavedChangesPromptProps={{
                  usePrompt,
                }}
                defaultValues={{
                  callback: () => {
                    const state = getDefaultFormValues(project);
                    return state;
                  },
                  deps: [mode, project],
                }}
                useFormMethods={useFormMethods}
                render={(formFieldMethods) => {
                  return (
                    <>
                      <DetailPageTopBar leftItems={topBarLeftItems} isReadOnly={isReadOnly} />
                      <Grid container spacing={2}>
                        <ProjectRequestDetailFormGeneralInfo
                          isReadOnly={isReadOnly}
                          mode={mode}
                          id={id || 0}
                          formFieldMethods={formFieldMethods}
                          disabledFields={[
                            EProjectRequestFormKeys.PROJECT_NO,
                            EProjectRequestFormKeys.PROJECT_REGION,
                            EProjectRequestFormKeys.PROJECT_TYPE,
                          ]}
                        />
                      </Grid>
                    </>
                  );
                }}
              />
            </>
          );
        }}
      />
      {renderArchiveConfirmation(
        isLoadingArchiveProject || isLoadingUnarchiveProject,
        isErrorArchiveProject || isErrorUnarchiveProject,
        isSuccessArchiveProject || isSuccessUnarchiveProject,
      )}
    </Scrollbar>
  );
};
